import React, { useEffect, useState } from 'react';

import { faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { navigate } from 'gatsby';
import Cookies from 'js-cookie';
import { Button, Input, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import styled from 'styled-components';
import { prop } from 'styled-tools';

import CtaButton from 'components/CtaButton/CtaButton';
import InnerHtml from 'components/InnerHtml/InnerHtml';

import { ModalI } from 'interfaces/content-stack/contentStack.types';

import { NEW_USER } from 'utils/constants';
import { dataLayerPush } from 'utils/functions';
import { getModularBlockLinkData } from 'utils/modularBlockUtils';

const StyledModal = styled(Modal)`
  top: 10%;

  .modal-content {
    border-radius: 0;
  }

  .modal-header {
    border: none;
    padding: 1.5rem 2.25rem 0 2.25rem;

    .modal-title {
      display: flex;
      justify-content: start;
      width: 100%;
    }
  }

  .modal-body {
    padding: 0.75rem 2.25rem 2.25rem 2.25rem;
  }
`;

const IconContainer = styled(Button)`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  height: 1.875rem;
  padding: 0;
  width: 1.875rem;

  &&& {
    padding: 0;
  }

  svg {
    color: ${prop('theme.colors.black')};
    font-size: 1.25rem;
  }
`;

const CheckboxOption = styled(Label)`
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  font-weight: 700;
  text-align: center;
  margin-left: 0.5rem;
  cursor: pointer;

  .form-check-input {
    position: relative;
    height: 1.2rem;
    width: 1.2rem;
    top: 0.2rem;
    margin-right: 0.5rem;
    margin-bottom: 0.6rem;
    cursor: pointer;
  }

  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const ActionOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 1.5rem;
  gap: 0.7rem;

  @media (max-width: ${prop('theme.breakpoints.md')}) {
    font-size: 0.8rem;
    margin-top: 0.5rem;
  }
`;

interface ModalWithActionsProps {
  data: Omit<ModalI, 'modalType'>;
}

const ModalWithActions: React.FC<ModalWithActionsProps> = ({ data }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isChecked, setIsChecked] = useState(false);

  if (!data) {
    return null;
  }

  const { actionOptions, body } = data.modalContent;
  const visitorCookie = Cookies.get('visitorCookie');
  const userDismissed = Cookies.get('rebrandModalCookie');

  useEffect(() => {
    if (visitorCookie === NEW_USER) {
      Cookies.set('rebrandModalCookie', 'rebrandModalDismissed', { expires: 3650 });
      dataLayerPush('rebrand_modal_opened');
    }

    if (userDismissed) {
      setIsOpen(false);
    }
  }, [visitorCookie]);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = 'unset';
    }
  }, [isOpen]);

  const handleDismissActions = () => {
    Cookies.set('rebrandModalCookie', 'rebrandModalDismissed', { expires: 3650 });
    setIsOpen(false);
  };

  const handleCheckboxChange = (link: string | undefined) => {
    setIsChecked(true);
    setTimeout(() => {
      navigate(link ?? '');
      handleDismissActions();
    }, 500);
  };

  return (
    <StyledModal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} backdrop='static'>
      <ModalHeader>
        <IconContainer color='link' onClick={handleDismissActions}>
          <FontAwesomeIcon icon={faClose} />
        </IconContainer>
      </ModalHeader>
      <ModalBody>
        <InnerHtml richText={body} />
        <ActionOptionsContainer>
          {actionOptions?.map((option, index) => {
            const { link, openInNewWindow } = getModularBlockLinkData(option.link);
            return option.actionStyle === 'checkbox' ? (
              <CheckboxOption key={option.label} check>
                <Input type='checkbox' checked={isChecked} onChange={() => handleCheckboxChange(link)} />
                {option.label}
              </CheckboxOption>
            ) : (
              <CtaButton
                key={index}
                onClick={handleDismissActions}
                to={link}
                openInNewWindow={openInNewWindow}
                text={option.label}
                buttonType={option.actionStyle}
                small
              />
            );
          })}
        </ActionOptionsContainer>
      </ModalBody>
    </StyledModal>
  );
};

export default ModalWithActions;
